<template>
  <div class="container mx-auto p-6 max-w-4xl">
    <div class="bg-white rounded-lg shadow-md p-6">
      <!-- Header with back button -->
      <div class="flex items-center mb-6">
        <button @click="goBack" class="mr-4 text-gray-600 hover:text-gray-800">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
        <h1 class="text-2xl font-bold text-gray-800">
          {{ editing ? 'Edit Tooltip' : 'Create New Tooltip' }}
        </h1>
      </div>

      <!-- Form -->
      <form @submit.prevent="submitForm" class="space-y-6">
        <!-- Page Field -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Page</label>
          <select 
            v-model="form.page" 
            :disabled="editing"
            class="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
            @click.stop
          >
            <option value="">Select a page</option>
            <option value="admin">Admin</option>
            <option value="waste">Waste</option>
            <option value="dashboard">Dashboard</option>
            <option value="REatlas">REatlas</option>
            <option value="CRESiteIQ">CRESiteIQ</option>
            <option value="projects">Projects</option>
            <option value="sites">Sites</option>
            <option value="reports">Reports</option>
            <option value="documents">Documents</option>
          </select>
          <p v-if="errors.page" class="mt-1 text-sm text-red-600">{{ errors.page[0] }}</p>
        </div>

        <!-- Section Field -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Section</label>
          <input 
            v-model="form.section" 
            type="text" 
            :disabled="editing"
            class="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
          >
          <p class="mt-1 text-xs text-gray-500">Write down the exact name: Example: Total Estimated Waste</p>
          <p v-if="errors.section" class="mt-1 text-sm text-red-600">{{ errors.section[0] }}</p>
        </div>

        <!-- Identifier Field -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Identifier (Unique Key)</label>
          <input 
            v-model="form.identifier" 
            type="text" 
            :disabled="editing"
            class="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
          >
          <p class="mt-1 text-xs text-gray-500">Example: dashboard-estimatedwaste-x</p>
          <p v-if="errors.identifier" class="mt-1 text-sm text-red-600">{{ errors.identifier[0] }}</p>
        </div>

        <!-- Content Field -->
        <!-- <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Content</label>
          <textarea 
            v-model="form.content" 
            rows="5" 
            class="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          ></textarea>
          <p v-if="errors.content" class="mt-1 text-sm text-red-600">{{ errors.content[0] }}</p>
        </div> -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Content</label>
          <vue-editor
            v-model="form.content"
            :editorOptions="editorOptions"
            class="bg-white rounded-md shadow-sm border border-gray-300 focus:border-blue-500"
          ></vue-editor>
          <p v-if="errors.content" class="mt-1 text-sm text-red-600">{{ errors.content[0] }}</p>
        </div>
        <!-- Form Actions -->
        <div class="flex justify-end space-x-4 pt-4 border-t border-gray-200">
          <button 
            type="button" 
            @click="goBack" 
            class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
         <button 
            type="submit" 
            :disabled="isSubmitting"
            class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-75"
          >
            <span v-if="isSubmitting">
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
            <span v-else>
              {{ editing ? 'Update Tooltip' : 'Create Tooltip' }}
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { VueEditor } from 'vue2-editor'

export default {
  components: { VueEditor },
  props: {
    tooltip: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      form: {
        page: '',
        section: '',
        identifier: '',
        content: ''
      },
      editorOptions: {
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'script': 'sub'}, { 'script': 'super' }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            [{ 'direction': 'rtl' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'font': [] }],
            [{ 'align': [] }],
            ['clean'],
            ['link', 'image']
          ]
        },
        placeholder: 'Enter tooltip content...',
        theme: 'snow'
      },
      errors: {},
      editing: false,
      isSubmitting: false
    }
  },
  created() {
    if (this.tooltip) {
      this.form = { ...this.tooltip }
      this.editing = true
    }
  },
  methods: {
    submitForm() {
      this.isSubmitting = true;
      this.errors = {};
      
      const url = this.editing 
        ? `/api/tooltips/${this.tooltip.id}`
        : '/api/tooltips';
        
      const method = this.editing ? 'put' : 'post';
      
      // Only send content in edit mode
      const data = this.editing 
        ? { content: this.form.content }
        : this.form;
      
      axios[method](url, data)
        .then(response => {
          this.$toast.success(`Tooltip ${this.editing ? 'updated' : 'created'} successfully!`, {
            timeout: 2000,
            onClose: () => {
              window.location.href = '/admin/tooltips';
            }
          });
        })
        .catch(error => {
          if (error.response.status === 422) {
            this.errors = error.response.data.errors;
          }
          this.$toast.error('Please fix the errors in the form.');
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    goBack() {
      window.location.href = '/admin/tooltips';
    }
  }
}
</script>

<style>
/* Import the editor CSS */
@import 'vue2-editor/dist/vue2-editor.css';
/* Import Quill theme CSS */
@import 'quill/dist/quill.core.css';
@import 'quill/dist/quill.snow.css';
</style>
